import { locales } from "@luntero/db/contracts/locales";

export { locales, type Locale } from "@luntero/db/contracts/locales";
import { defineRouting } from "next-intl/routing";
import { createNavigation } from "next-intl/navigation";
import { createElement, forwardRef, type ComponentProps } from "react";
import { env } from "@/lib/env";

export const routing = defineRouting({
  locales,
  defaultLocale: "en",
  localePrefix: "as-needed",
  // Dynamic article and glossary slugs are localized in the database. The
  // middleware can only reuse the current pathname for every locale, which
  // produces invalid hreflang URLs for those routes. Pages emit their own
  // route-aware alternates from metadata instead.
  alternateLinks: false,
  // English is the default locale and therefore unprefixed, so Accept-Language
  // detection cannot tell "chose English" from "hasn't chosen yet" and would
  // redirect those users to their browser language on every unprefixed URL.
  // The cookie records an explicit choice and takes priority over detection.
  localeCookie: true,
});

export const localeCodes = [
  {
    locale: "en",
    code: "en-US",
    url: "",
    name: "English",
    emoji: "🇺🇸",
    flag: "/assets/flag/uk.webp",
  },
  {
    locale: "nl",
    code: "nl-NL",
    url: "/nl",
    name: "Nederlands",
    emoji: "🇳🇱",
    flag: "/assets/flag/nl.webp",
  },
  {
    locale: "fr",
    code: "fr-FR",
    url: "/fr",
    name: "Français",
    emoji: "🇫🇷",
    flag: "/assets/flag/fr.webp",
  },
  {
    locale: "es",
    code: "es-ES",
    url: "/es",
    name: "Español",
    emoji: "🇪🇸",
    flag: "/assets/flag/es.webp",
  },
  {
    locale: "de",
    code: "de-DE",
    url: "/de",
    name: "Deutsch",
    emoji: "🇩🇪",
    flag: "/assets/flag/de.webp",
  },
];

export const getLocalePath = (locale: string) => {
  return locale === "en" ? "" : `/${locale}`;
};

const getLocalizedPath = (locale: string, path = "/") => {
  const localePath = getLocalePath(locale);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (normalizedPath === "/") {
    return localePath || "/";
  }

  return `${localePath}${normalizedPath}`;
};

export const getLocaleHref = (locale: string, path = "/") => {
  return getLocalizedPath(locale, path);
};

export const getAlternalteLinks = (href: string): Record<string, string> => {
  return localeCodes.reduce(
    (acc, item) => {
      acc[item.code] =
        env.NEXT_PUBLIC_URL + getLocalizedPath(item.locale, href);
      return acc;
    },
    {} as Record<string, string>,
  );
};

export const getFullLocalePath = (
  locale: string,
  path = "/",
  basePath = env.NEXT_PUBLIC_URL,
) => {
  return `${basePath}${getLocalizedPath(locale, path)}`;
};

const navigation = createNavigation(routing);

type NavigationLinkProps = ComponentProps<typeof navigation.Link>;

export const Link = forwardRef<HTMLAnchorElement, NavigationLinkProps>(
  function Link({ prefetch = false, ...props }, ref) {
    return createElement(navigation.Link, { ref, prefetch, ...props });
  },
);

export const { redirect, usePathname, useRouter, getPathname } = navigation;
